import { useState, useEffect, useCallback } from 'react'
import { MessageSquare, Plus, Trash2, X, User } from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { formatDistanceToNow } from 'date-fns'

// General check-in notes for a client — anything worth recording that isn't
// tied to a goal or a commitment. Self-contained: loads its own notes and the
// shared tag list, so it can drop into the 90-day view and the client profile
// without either parent needing to know about it.

const relTime = (ts) => {
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }) }
  catch { return '' }
}

// Palette offered when someone creates a new tag.
const TAG_COLORS = [
  '#b4453c', '#2e6e3a', '#c4962a', '#2a6e84', '#8c4d78', '#6b6b6b', '#4a5a8c', '#a05a2c',
]

export default function ClientNotesPanel({ clientId, currentUser, profiles = [], compact = false }) {
  const toast = useToast()
  const [notes, setNotes] = useState([])
  const [tags, setTags] = useState([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [body, setBody] = useState('')
  const [tagId, setTagId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [filterTag, setFilterTag] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)

  // New-tag creation, inline in the composer.
  const [newTagOpen, setNewTagOpen] = useState(false)
  const [newTagLabel, setNewTagLabel] = useState('')
  const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0])

  const load = useCallback(async () => {
    if (!clientId) return
    setLoading(true)
    const [nRes, tRes] = await Promise.all([
      supabase.from('client_notes').select('*').eq('client_id', clientId)
        .order('created_at', { ascending: false }),
      supabase.from('note_tags').select('*').order('label'),
    ])
    if (nRes.error) toast('Could not load notes: ' + nRes.error.message, 'error')
    setNotes(nRes.data || [])
    setTags(tRes.data || [])
    setLoading(false)
  }, [clientId, toast])

  useEffect(() => { load() }, [load])

  const tagById = id => tags.find(t => t.id === id)

  const save = async () => {
    const text = body.trim()
    if (!text) return
    setSaving(true)
    const { error } = await supabase.from('client_notes').insert({
      client_id: clientId, body: text, tag_id: tagId,
      author_id: currentUser?.id || null,
    })
    setSaving(false)
    if (error) { toast('Could not save note: ' + error.message, 'error'); return }
    setBody(''); setTagId(null); setAdding(false)
    load()
  }

  const createTag = async () => {
    const label = newTagLabel.trim()
    if (!label) return
    const existing = tags.find(t => t.label.toLowerCase() === label.toLowerCase())
    if (existing) { setTagId(existing.id); setNewTagOpen(false); setNewTagLabel(''); return }
    const { data, error } = await supabase.from('note_tags')
      .insert({ label, color: newTagColor }).select().single()
    if (error) { toast('Could not create tag: ' + error.message, 'error'); return }
    setTags(prev => [...prev, data].sort((a, b) => a.label.localeCompare(b.label)))
    setTagId(data.id)
    setNewTagOpen(false); setNewTagLabel('')
  }

  const doDelete = async () => {
    const { error } = await supabase.from('client_notes').delete().eq('id', confirmDelete.id)
    if (error) { toast('Could not delete: ' + error.message, 'error'); return }
    setConfirmDelete(null)
    load()
  }

  const visible = filterTag ? notes.filter(n => n.tag_id === filterTag) : notes
  // Only offer filters for tags actually used on this client's notes.
  const usedTagIds = [...new Set(notes.map(n => n.tag_id).filter(Boolean))]

  return (
    <div className="card" style={{ marginTop: compact ? 0 : 14 }}>
      <div className="card-header">
        <span className="card-title"><MessageSquare size={14} /> Check-in notes</span>
        <button className="btn btn-ghost btn-sm" onClick={() => setAdding(a => !a)}>
          {adding ? <><X size={12} /> Cancel</> : <><Plus size={12} /> Add note</>}
        </button>
      </div>

      <div className="card-body">
        {adding && (
          <div style={{ marginBottom: 14, padding: 12, background: 'var(--cream)', borderRadius: 8 }}>
            <textarea className="input" rows={4} autoFocus
              placeholder="What came up? e.g. leads are coming through but they aren't converting them"
              value={body} onChange={e => setBody(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box', display: 'block', minHeight: 90,
                resize: 'vertical', marginBottom: 10, lineHeight: 1.55,
                fontFamily: 'inherit', fontSize: 13.5,
              }} />

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
              {tags.map(t => (
                <button key={t.id} type="button"
                  onClick={() => setTagId(tagId === t.id ? null : t.id)}
                  style={{
                    fontSize: 11.5, padding: '3px 10px', borderRadius: 11, cursor: 'pointer',
                    border: `1.5px solid ${t.color}`,
                    background: tagId === t.id ? t.color : 'transparent',
                    color: tagId === t.id ? '#fff' : t.color,
                    fontWeight: tagId === t.id ? 600 : 500,
                  }}>
                  {t.label}
                </button>
              ))}
              {!newTagOpen && (
                <button type="button" className="btn btn-ghost btn-sm"
                  style={{ fontSize: 11.5, padding: '2px 8px' }}
                  onClick={() => setNewTagOpen(true)}>
                  <Plus size={11} /> New tag
                </button>
              )}
            </div>

            {newTagOpen && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                <input className="input" placeholder="Tag name" value={newTagLabel}
                  onChange={e => setNewTagLabel(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); createTag() } }}
                  style={{ width: 160 }} />
                <div style={{ display: 'flex', gap: 4 }}>
                  {TAG_COLORS.map(c => (
                    <button key={c} type="button" onClick={() => setNewTagColor(c)}
                      title={c}
                      style={{
                        width: 20, height: 20, borderRadius: '50%', background: c, cursor: 'pointer',
                        border: newTagColor === c ? '2.5px solid var(--navy)' : '1px solid rgba(0,0,0,0.15)',
                      }} />
                  ))}
                </div>
                <button className="btn btn-ghost btn-sm" onClick={createTag}>Create</button>
                <button className="btn btn-ghost btn-sm" onClick={() => { setNewTagOpen(false); setNewTagLabel('') }}>
                  Cancel
                </button>
              </div>
            )}

            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !body.trim()}>
              {saving ? 'Saving…' : 'Save note'}
            </button>
          </div>
        )}

        {usedTagIds.length > 1 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            <button type="button" onClick={() => setFilterTag(null)}
              style={{
                fontSize: 11, padding: '2px 9px', borderRadius: 10, cursor: 'pointer',
                border: '1.5px solid var(--border)',
                background: filterTag === null ? 'var(--navy)' : 'transparent',
                color: filterTag === null ? '#fff' : 'var(--text-secondary)',
              }}>
              All ({notes.length})
            </button>
            {usedTagIds.map(id => {
              const t = tagById(id)
              if (!t) return null
              const count = notes.filter(n => n.tag_id === id).length
              return (
                <button key={id} type="button" onClick={() => setFilterTag(filterTag === id ? null : id)}
                  style={{
                    fontSize: 11, padding: '2px 9px', borderRadius: 10, cursor: 'pointer',
                    border: `1.5px solid ${t.color}`,
                    background: filterTag === id ? t.color : 'transparent',
                    color: filterTag === id ? '#fff' : t.color,
                  }}>
                  {t.label} ({count})
                </button>
              )
            })}
          </div>
        )}

        {loading ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>
        ) : visible.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            {notes.length === 0
              ? 'Nothing logged yet. Use this for anything that comes up between check-ins.'
              : 'No notes with that tag.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {visible.map(n => {
              const t = tagById(n.tag_id)
              const author = profiles.find(p => p.id === n.author_id)
              return (
                <div key={n.id} style={{
                  padding: '10px 12px', background: 'var(--cream)', borderRadius: 8,
                  borderLeft: `3px solid ${t?.color || 'var(--border)'}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                    {t && (
                      <span style={{
                        fontSize: 10.5, fontWeight: 600, padding: '1px 8px', borderRadius: 10,
                        background: t.color, color: '#fff',
                      }}>{t.label}</span>
                    )}
                    <span style={{
                      fontSize: 10.5, fontWeight: 600, padding: '1px 8px', borderRadius: 10,
                      background: 'var(--navy)', color: '#fff', display: 'inline-flex',
                      alignItems: 'center', gap: 4,
                    }}>
                      <User size={10} /> {author?.full_name || 'Unknown'}
                    </span>
                    <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                      {relTime(n.created_at)}
                    </span>
                    <button className="btn btn-ghost btn-sm" title="Delete note"
                      style={{ marginLeft: 'auto', color: 'var(--red)', padding: '2px 6px' }}
                      onClick={() => setConfirmDelete(n)}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                  <div style={{ fontSize: 13.5, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{n.body}</div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Delete this note?</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(null)}><X size={14} /></button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13.5, lineHeight: 1.6 }}>This cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={doDelete}><Trash2 size={13} /> Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}